import { useMemo } from "react";

import { isSameDay } from "date-fns";
import { uniq } from "lodash";

import useGetProposal from "@fartherfinance/frontend/api/Investment/hooks/useGetProposal";
import { TradeProposalId } from "@fartherfinance/frontend/api/Investment/hooks/useRelatedTradeProposals";
import { EnhancedTradeProposal } from "@fartherfinance/frontend/api/Investment/request/getTradeProposal";
import useSecuritiesBySecurityIds from "@fartherfinance/frontend/api/SecurityMaster/hooks/useSecuritiesBySecurityIds";
import { Response as SecuritiesResponse } from "@fartherfinance/frontend/api/SecurityMaster/requests/getSecuritiesBySecurityIds";
import { HookResult } from "@fartherfinance/frontend/api/Types";

import useAdvisorRequestAuth from "@src/multiCustodian/hooks/useAdvisorRequestAuth";

type MergeTradeWithSecurity = {
  trade: EnhancedTradeProposal["trades"][number];
  security: SecuritiesResponse[number];
};

type SubmittedTradesResponse = {
  trades: MergeTradeWithSecurity[] | null;
  tradeProposal: EnhancedTradeProposal;
};

type RelatedTradesResult = HookResult<SubmittedTradesResponse>;

const useSubmittedRelatedTrades = (
  tradeProposalId: TradeProposalId,
  submittedAt?: Date
): RelatedTradesResult => {
  const auth = useAdvisorRequestAuth();
  const tradeProposal = useGetProposal(tradeProposalId, auth);

  const allTradeSecIds = useMemo(() => {
    if (
      tradeProposal.data?.trades === undefined ||
      tradeProposal.data.submittedAt === null
    ) {
      return null;
    }

    // if submittedAt is defined, check if the trade proposal was submitted on the selected date
    if (
      submittedAt !== undefined &&
      isSameDay(submittedAt, tradeProposal.data.submittedAt) === false
    ) {
      return null;
    }

    const secIds = tradeProposal.data.trades.map((trade) => {
      return trade.securityId;
    });

    return uniq(secIds);
  }, [
    tradeProposal.data?.trades,
    tradeProposal.data?.submittedAt,
    submittedAt,
  ]);

  const tradeSecurityRequest = useSecuritiesBySecurityIds(allTradeSecIds, auth);

  const mergedTradeWithSecurity: MergeTradeWithSecurity[] | null =
    useMemo(() => {
      if (
        tradeProposal.data === undefined ||
        tradeSecurityRequest.data === undefined
      ) {
        return null;
      }

      return tradeProposal.data.trades.map((trade) => {
        const sec = tradeSecurityRequest.data.find(
          (sec) => sec.id === trade.securityId
        );

        if (sec === undefined) {
          throw new Error(`Security ID ${trade.securityId} not found`);
        }

        return {
          trade,
          security: sec,
        };
      });
    }, [tradeProposal.data, tradeSecurityRequest?.data]);

  return useMemo(() => {
    // if any of the apis are loading return loading
    if (
      tradeProposal.isLoading ||
      (allTradeSecIds !== null && tradeSecurityRequest.isLoading)
    ) {
      return {
        isLoading: true,
        data: undefined,
        hasError: false,
      };
    }

    // if there is an error in any of them return error
    if (tradeProposal.hasError || tradeSecurityRequest.hasError) {
      return {
        isLoading: false,
        data: undefined,
        hasError: true,
        error: new Error("Failed to load related trades"),
      };
    }

    return {
      isLoading: false,
      hasError: false,
      data: {
        trades: mergedTradeWithSecurity,
        tradeProposal: tradeProposal.data,
      },
    };
  }, [
    allTradeSecIds,
    mergedTradeWithSecurity,
    tradeProposal.data,
    tradeProposal.hasError,
    tradeProposal.isLoading,
    tradeSecurityRequest.hasError,
    tradeSecurityRequest.isLoading,
  ]);
};

export default useSubmittedRelatedTrades;
